import { h, Component, ComponentType, RefCallback } from "preact";
import noderef from "../../foundation/utils/noderef";
import { preventDefault } from "../../foundation/utils/legacy";
import { Keyboard } from "../../foundation/enums";
import BasicRecord from "../../foundation/utils/BasicRecord";
import CSSProperties from "../../foundation/model/CSSProperties";
import { DragPosition } from "./types";

/**
 * P: props, T: drag data, MP: mirror props
 */
interface DragSourceOptions<P, T = unknown, MP = unknown> {
    itemType: string;
    mirrorComponent: ComponentType<MP>;
    mirrorZIndex?: number;
    allowX?: boolean;
    allowY?: boolean;
    class?: string;
    style?: CSSProperties;
    calculateDragOffset?: (props: P) => DragPosition | null;
    staticDragOffset?: (props: P) => DragPosition | null;
    provideDragData: (props: P) => T;
    getSourceRef?: (props: P) => HTMLElement | null;
    onStartDrag?: (props: P) => void;
    onEndDrag?: (props: P) => void;
}

interface DragSourceRequiredProps {
    dragBaseRef?: RefCallback<HTMLElement>;
}

interface DragSourceInjectedProps {
    dragging?: boolean;
}

type DragSourceProps = DragSourceRequiredProps & DragSourceInjectedProps;

interface DragSourceState {
    dragging: boolean;
    originX?: number;
    originY?: number;
}

const DEFAULT_MOVING_THRESHOLD = 12;
const DEFAULT_OPTIONS = {
    allowX: true,
    allowY: true,
    movingThreshold: DEFAULT_MOVING_THRESHOLD
};

/**
 * P: props, T: drag data, MP: mirror props
 */
function DragSource<P extends DragSourceRequiredProps, T = P, MP = P>(
    optionsParam: DragSourceOptions<P, T, MP>
) {
    const options = Object.assign({}, DEFAULT_OPTIONS, optionsParam);

    return (WrappedComponent: ComponentType<P>): ComponentType<Omit<P, keyof DragSourceInjectedProps>> =>
        class extends Component<Omit<P, keyof DragSourceInjectedProps>, DragSourceState> {
            override state: DragSourceState = {
                dragging: false
            };

            nodeRefs: BasicRecord<HTMLElement | null> = {};
            initialPosition = {
                top: 0,
                left: 0
            };

            handleBaseRef = (el: HTMLElement | null) => {
                const { dragBaseRef } = this.props;

                noderef(this, "base")(el);
                dragBaseRef?.(el);
            };

            handleMouseMove = (ev: MouseEvent) => {
                if (this.state.dragging) {
                    ev.preventDefault();
                    this.handleDrag(ev);
                    return;
                }

                const horizontalDistance = options.allowX ? Math.abs(ev.clientX - this.state.originX!) : 0;
                const verticalDistance = options.allowY ? Math.abs(ev.clientY - this.state.originY!) : 0;
                if (Math.hypot(horizontalDistance, verticalDistance) > options.movingThreshold) {
                    this.handleDragStart(ev);
                }
            };

            handleKeyDown = (ev: KeyboardEvent) => {
                if (ev.key === Keyboard.Key.Escape) {
                    if (!this.state.dragging) {
                        return;
                    }

                    this.handleDragEnd();
                }
            };

            handleMouseDown = (ev: MouseEvent) => {
                if (ev.button !== Zeplin.MOUSE_BUTTON.LEFT || this.context.dragDisabled) {
                    return;
                }

                ev.stopPropagation(); // what if this breaks some things?

                this.setState({
                    originX: ev.clientX,
                    originY: ev.clientY
                });
                document.addEventListener("keydown", this.handleKeyDown);
                document.addEventListener("mousemove", this.handleMouseMove);
                document.addEventListener("mouseup", this.handleMouseUp);
            };

            handleMouseUp = (ev: MouseEvent) => {
                document.removeEventListener("keydown", this.handleKeyDown);
                document.removeEventListener("mousemove", this.handleMouseMove);
                document.removeEventListener("mouseup", this.handleMouseUp);

                if (!this.state.dragging) {
                    return;
                }

                this.handleDragEnd();
                ev.preventDefault();
            };

            handleDragStart = (ev: MouseEvent) => {
                let sourceRef = this.nodeRefs["base"]!;
                if (options.getSourceRef) {
                    sourceRef = options.getSourceRef(this.props as P) || sourceRef;
                }
                const initialPositionX = options.allowX ? ev.clientX : this.state.originX!;
                const initialPositionY = options.allowY ? ev.clientY : this.state.originY!;
                const sourceRect = sourceRef.getBoundingClientRect();
                const calculatedDragOffset = options.calculateDragOffset
                    ? options.calculateDragOffset(this.props as P)
                    : null;
                const dragOffset = calculatedDragOffset || {
                    left: initialPositionX - sourceRect.left,
                    top: initialPositionY - sourceRect.top
                };
                const passObject = options.provideDragData(this.props as P);
                this.context.onStartDrag({
                    draggingItemType: options.itemType,
                    sourceRect,
                    mirrorComponent: options.mirrorComponent,
                    mirrorZIndex: options.mirrorZIndex,
                    dragData: passObject,
                    dragOffset,
                    position: {
                        left: initialPositionX,
                        top: initialPositionY
                    }
                });

                if (options.onStartDrag) {
                    options.onStartDrag(this.props as P);
                }

                this.setState({
                    dragging: true
                });

                this.initialPosition = {
                    left: initialPositionX,
                    top: initialPositionY
                };
            };

            handleDrag = (ev: MouseEvent) => {
                this.context.onDrag({
                    left: options.allowX ? ev.clientX : this.initialPosition.left,
                    top: options.allowY ? ev.clientY : this.initialPosition.top
                });
            };

            handleDragEnd = () => {
                this.context.onEndDrag();

                if (options.onEndDrag) {
                    options.onEndDrag(this.props as P);
                }

                this.setState({
                    dragging: false
                });

                this.initialPosition = {
                    top: 0,
                    left: 0
                };
            };

            render() {
                return (
                    <div
                        class={options.class}
                        style={options.style}
                        ref={this.handleBaseRef}
                        onMouseDown={this.handleMouseDown}
                        onDragStart={preventDefault}>
                        <WrappedComponent
                            {...(this.props as P)}
                            dragging={this.state.dragging}/>
                    </div>
                );
            }
        };
}

export { DragSourceProps };
export default DragSource;
